import React, { Component } from 'react';
import { buildPath } from '../utils/route_utils.js';
import { updateSEO } from '../utils/seo_utils';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import Breadcrumb from '../components/Breadcrumb';
import LogoWall from '../components/LogoWall';
import '../css/CollaboratePage.css';

class CollaboratePage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: this.props.lang,
      visibleSections: {},
      activeTab: "standard"
    };
    this.update_lang = this.update_lang.bind(this);
    this.sectionRefs = [];
    this.observerCallback = this.observerCallback.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    updateSEO('colab_flow', this.state.lang, '/' + this.state.lang + '/about/colab_flow');
    this.observer = new IntersectionObserver(this.observerCallback, {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    });
    this.sectionRefs.forEach(ref => {
      if (ref) this.observer.observe(ref);
    });
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
  }

  observerCallback(entries) {
    const updates = {};
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        updates[entry.target.dataset.section] = true;
      }
    });
    if (Object.keys(updates).length > 0) {
      this.setState(prev => ({ visibleSections: { ...prev.visibleSections, ...updates } }));
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) {
      this.setState({ lang: nextProps.lang });
    }
  }

  update_lang(newLang) { this.props.updateLang(newLang); }

  navigate(route) { window.location.href = buildPath(this.state.lang, route); }

  registerRef(el, id) {
    if (el && !this.sectionRefs.includes(el)) {
      el.dataset.section = id;
      this.sectionRefs.push(el);
      if (this.observer) this.observer.observe(el);
    }
  }

  render() {
    const lang = this.state.lang;
    const vis = this.state.visibleSections;
    const { activeTab } = this.state;

    const hero = {
      tag: { zh: "COLLABORATION", en: "COLLABORATION", ja: "COLLABORATION" },
      title: {
        zh: "合作流程",
        en: "How to Collaborate?",
        ja: "協力方法"
      },
      subtitle: {
        zh: "從需求確認到系統上線，全程由 Nexuni 專業團隊協助導入",
        en: "From requirement analysis to system launch, guided by the Nexuni team every step of the way",
        ja: "要件確認からシステム稼働まで、Nexuni の専門チームが全面サポート"
      }
    };

    const tabLabels = {
      standard: { zh: "標準合作", en: "Standard Collaboration", ja: "標準コラボレーション" },
      launch: { zh: "首發客戶", en: "Launch Customer", ja: "ローンチカスタマー" }
    };

    const introText = {
      standard: {
        zh: "Nexuni 專注於 AI 機器人自動化技術，致力於協助企業解決人力短缺與高風險作業的挑戰，透過智慧機器人全面提升營運效率與現場安全。我們的系統廣泛應用於工廠、公寓社區、停車場等多種場域，能針對不同場景提供模組化與客製化的自動化解決方案。",
        en: "Nexuni focuses on AI robotic automation technology, helping businesses address challenges such as labor shortages and high-risk operations. By leveraging intelligent robots, we enhance operational efficiency and on-site safety across various industries. Our systems are widely used in factories, residential communities, and parking facilities, offering modular and customizable automation solutions tailored to each environment.",
        ja: "Nexuni は AI ロボット自動化技術に特化し、人手不足や高リスク作業といった課題の解決を企業に提供しています。スマートロボットを活用することで、業務効率と現場の安全性を総合的に向上させます。当社のシステムは、工場、マンション、駐車場など、さまざまな現場で活用されており、場面に応じたモジュール式かつカスタマイズ可能な自動化ソリューションを提供します。"
      },
      launch: {
        zh: "如果市場上現有產品無法滿足您的需求，Nexuni 提供首發客戶合作方案。我們將派駐工程師至您的現場，深入了解實際的人力挑戰與流程痛點，並從零開始為您設計、開發量身打造的機器人或自動化產品。這就像航空公司訂購新型客機一樣——您定義需求，我們打造解決方案。",
        en: "When off-the-shelf products don't fit your needs, Nexuni offers a Launch Customer partnership. We forward-deploy engineers to your site, study your labor challenges and workflow pain points firsthand, and design and develop a robot or automation product built specifically for you. Think of it like an airline ordering a new aircraft — you define the requirements, we build the solution.",
        ja: "既製品ではニーズに合わない場合、Nexuni はローンチカスタマー・パートナーシップを提供します。エンジニアを現場に派遣し、人手不足やワークフローの課題を直接把握した上で、ゼロからロボットや自動化製品を設計・開発します。航空会社が新型機を発注するように——要件を定義いただき、私たちがソリューションを構築します。"
      }
    };

    const introText2 = {
      standard: {
        zh: "合作導入流程從選擇適合的產品開始。客戶可參考 Nexuni 產品型錄，依據需求選定合適的解決方案，接著與我們預約諮詢時間，由專業團隊進行需求確認與場域評估，進而提供最適報價與系統規劃。完成確認後，即安排安裝整合與操作訓練，協助客戶快速啟用智慧自動化系統。",
        en: "The collaboration process begins with choosing the suitable product. Customers can refer to the Nexuni product catalog to choose a suitable solution based on their needs, then schedule a consultation with us. Our expert team will assess the site and confirm the requirements, providing the optimal quotation and system plan. Once confirmed, we proceed with installation, system integration, and operational training to help customers quickly deploy their intelligent automation system.",
        ja: "ロボット導入の流れは、適切な製品の選定から始まります。お客様は Nexuni の製品カタログを参考にニーズに合ったソリューションを選び、その後、弊社にご連絡いただき、相談の予約をしていただきます。専門チームがニーズの確認と現場調査を行い、最適な見積もりとシステム計画をご提案します。確認が完了すると、設置統合および操作トレーニングを実施し、お客様が迅速にスマート自動化システムを導入できるようサポートします。"
      },
      launch: {
        zh: "首發客戶合作從簽署合作備忘錄（MOU）開始。Nexuni 將派駐工程師至您的營運現場，實地了解作業環境、人力瓶頸與流程需求。接著，我們的研發團隊將依據調查結果設計並開發專屬的機器人或自動化方案。開發完成後，以最低訂購量進行報價，最終由 Nexuni 負責部署上線，並提供持續的監控、維護與 AI 模型更新服務，確保系統隨時間持續優化。",
        en: "The Launch Customer partnership begins with signing a Memorandum of Understanding (MOU). Nexuni forward-deploys engineers to your operational site to study the working environment, labor bottlenecks, and process requirements firsthand. Our R&D team then designs and develops a robot or automation solution based on these findings. Once development is complete, a quotation is provided with minimum order quantity. Finally, Nexuni handles full deployment and delivers ongoing monitoring, maintenance, and AI model updates to ensure the system continuously improves over time.",
        ja: "ローンチカスタマー・パートナーシップは、覚書（MOU）の締結から始まります。Nexuni のエンジニアがお客様の現場に赴き、作業環境、人手不足のボトルネック、プロセス要件を直接調査します。その結果を基に、R&D チームが専用のロボットまたは自動化ソリューションを設計・開発します。開発完了後、最小発注数での見積もりをご提示し、Nexuni が導入を担当。継続的な監視・保守・AI モデル更新で、システムの長期的な最適化を保証します。"
      }
    };

    const standardSteps = [
      {
        num: "01",
        title: { zh: "選擇產品", en: "Select", ja: "製品選定" },
        desc: { zh: "從 Nexuni 產品型錄中挑選適合的解決方案，並聯絡我們。", en: "Identify the solutions of your interests from Nexuni's catalog and contact us.", ja: "Nexuniのカタログから興味のあるソリューションを選択し、ご連絡ください。" },
        color: "var(--sub2-color)"
      },
      {
        num: "02",
        title: { zh: "諮詢討論", en: "Consult", ja: "コンサルティング" },
        desc: { zh: "預約會議或現場參訪以確認需求與場域評估。", en: "Schedule a meeting or visit to confirm requirements and assess your site.", ja: "要件確認と現場評価のための会議や訪問を予約。" },
        color: "var(--sub2-color)"
      },
      {
        num: "03",
        title: { zh: "取得報價", en: "Quote", ja: "見積もり" },
        desc: { zh: "我們會根據需求回傳量身打造的報價。", en: "We will send a quotation tailored to your requirements.", ja: "要件に基づいたカスタマイズ見積もりをお送りします。" },
        color: "var(--sub2-color)"
      },
      {
        num: "04",
        title: { zh: "部署上線", en: "Deploy", ja: "導入展開" },
        desc: { zh: "根據合作專案時程做系統安裝整合與教育訓練。", en: "System installation and integration according to the agreed timeline.", ja: "合意したスケジュールに沿ってシステム設置と統合。" },
        color: "var(--sub2-color)"
      }
    ];

    const launchSteps = [
      {
        num: "01",
        title: { zh: "簽署 MOU & 前線部署", en: "MOU & Forward Deploy", ja: "MOU 締結 & 現場派遣" },
        desc: { zh: "簽署合作備忘錄，Nexuni 派駐工程師至現場了解人力挑戰。", en: "Sign MOU and Nexuni sends engineers to your site to understand labor challenges.", ja: "MOUを締結し、Nexuniのエンジニアを現場に派遣して課題を把握。" },
        color: "var(--sub1-color)"
      },
      {
        num: "02",
        title: { zh: "設計與開發", en: "Design & Develop", ja: "設計・開発" },
        desc: { zh: "針對客戶需求設計並開發專屬的機器人與自動化產品。", en: "Design and develop a robot or automation product tailored to your specific needs.", ja: "ニーズに合わせたロボット・自動化製品を設計・開発。" },
        color: "var(--sub1-color)"
      },
      {
        num: "03",
        title: { zh: "最低訂購量報價", en: "Quote with MOQ", ja: "最小発注数での見積" },
        desc: { zh: "根據開發成果提供最低訂購量報價。", en: "Receive a quotation with minimum order quantity.", ja: "最小発注数での見積もりを受け取る。" },
        color: "var(--sub1-color)"
      },
      {
        num: "04",
        title: { zh: "部署與持續進化", en: "Deploy & Evolve", ja: "導入・継続進化" },
        desc: { zh: "系統部署並提供持續監控、維護與 AI 學習適應新場景。", en: "Deploy with continuous monitoring, maintenance, and AI learning for new scenarios.", ja: "導入後も継続的な監視・保守・AI学習で進化。" },
        color: "var(--sub1-color)"
      }
    ];

    const steps = activeTab === "standard" ? standardSteps : launchSteps;

    const standardExampleTitle = {
      zh: "合作範例：AI 保全巡邏機器狗",
      en: "Example: AI Security Patrol Dog",
      ja: "協力例：AI警備巡回ロボット犬"
    };

    const standardExampleSteps = {
      zh: [
        { heading: "1. 選擇產品", body: "保全公司或社區管理業者面臨夜間巡邏人力不足問題，在 Nexuni 產品型錄中看到 AI 保全巡邏機器狗解決方案，主動聯繫 Nexuni 了解詳情。" },
        { heading: "2. 諮詢討論", body: "Nexuni 業務與技術團隊安排現場參訪，評估巡邏路線、地形條件、建築物配置、監控系統整合需求，以及作業環境（如室內外、坡度、樓梯等），確認最適合的部署方案。" },
        { heading: "3. 取得報價", body: "根據場域評估結果，Nexuni 提供完整報價，包含巡邏機器狗數量、充電站配置、監控儀表板軟體授權，以及操作訓練與售後支援服務方案。" },
        { heading: "4. 部署上線", body: "雙方確認合作後，Nexuni 進行系統安裝與現場部署，培訓保全人員操作機器狗與監控介面。系統上線後，巡邏機器狗即可依照預設路線執行 24 小時自主巡邏任務。" }
      ],
      en: [
        { heading: "1. Select", body: "A security company or community management firm faces nighttime patrol staffing shortages. They discover the AI Security Patrol Dog in Nexuni's product catalog and reach out to learn more." },
        { heading: "2. Consult", body: "Nexuni's sales and technical teams schedule a site visit to assess patrol routes, terrain conditions, building layout, surveillance system integration needs, and operating environment (indoor/outdoor, slopes, stairs, etc.) to determine the optimal deployment plan." },
        { heading: "3. Quote", body: "Based on the site assessment, Nexuni provides a complete quotation covering patrol dog units, charging stations, monitoring dashboard software licenses, operational training, and after-sales support." },
        { heading: "4. Deploy", body: "Once confirmed, Nexuni handles system installation and on-site deployment, training security staff on robot dog operation and monitoring interfaces. After launch, the patrol dogs autonomously execute 24/7 patrol missions along preset routes." }
      ],
      ja: [
        { heading: "1. 製品選定", body: "警備会社やマンション管理会社が夜間巡回の人手不足に直面。Nexuni の製品カタログで AI 巡回ロボット犬ソリューションを発見し、詳細を問い合わせます。" },
        { heading: "2. コンサルティング", body: "Nexuni の営業・技術チームが現場を訪問し、巡回ルート、地形条件、建物配置、監視システムとの統合要件、作業環境（屋内外、傾斜、階段など）を評価し、最適な導入プランを策定します。" },
        { heading: "3. 見積もり", body: "現場評価に基づき、巡回ロボット犬の台数、充電ステーション、監視ダッシュボードのソフトウェアライセンス、操作トレーニング、アフターサポートを含む総合見積もりを提示します。" },
        { heading: "4. 導入展開", body: "導入決定後、Nexuni がシステム設置と現場デプロイを実施し、警備スタッフにロボット犬の操作と監視インターフェースのトレーニングを行います。稼働開始後、巡回ロボット犬が設定ルートに沿って 24 時間自律巡回を実行します。" }
      ]
    };

    const launchExampleTitle = {
      zh: "合作範例：智慧焊接機器人（首發客戶）",
      en: "Example: Smart Welding Robot (Launch Customer)",
      ja: "協力例：スマート溶接ロボット（ローンチカスタマー）"
    };

    const launchExampleSteps = {
      zh: [
        { heading: "1. 簽署 MOU & 前線部署", body: "金屬加工廠面臨嚴重的焊工人力短缺問題，與 Nexuni 簽署合作備忘錄。Nexuni 隨即派駐工程師至客戶工廠現場，實地了解焊接流程、人力瓶頸、工件種類與現有設備情況，掌握第一手的作業挑戰。" },
        { heading: "2. 設計與開發", body: "根據前線調查結果，Nexuni 研發團隊設計一套結合雷射掃描與協作式機械手臂的智慧焊接機器人系統。系統能自動修正工件誤差，無需專業焊工即可操作，解決客戶最核心的人力問題。" },
        { heading: "3. 最低訂購量報價", body: "開發完成後，Nexuni 根據系統規格與客戶的產線規模，提供最低訂購量報價，涵蓋硬體設備、軟體授權、系統整合與教育訓練。" },
        { heading: "4. 部署與持續進化", body: "系統在客戶工廠完成安裝部署，Nexuni 提供現場操作訓練。上線後，系統透過 AI 持續學習功能，不斷優化焊接路徑與參數，自動適應新工件類型，讓焊接品質與產線效率持續提升。" }
      ],
      en: [
        { heading: "1. MOU & Forward Deploy", body: "A metal fabrication factory facing severe welder labor shortages signs an MOU with Nexuni. Nexuni immediately forward-deploys engineers to the client's factory floor to study the welding processes, labor bottlenecks, workpiece types, and existing equipment firsthand." },
        { heading: "2. Design & Develop", body: "Based on the on-site findings, Nexuni's R&D team designs a smart welding robot system combining laser scanning with a collaborative robotic arm. The system automatically corrects workpiece deviations, eliminating the need for skilled welders and directly addressing the client's core labor challenge." },
        { heading: "3. Quote with MOQ", body: "Once development is complete, Nexuni provides a quotation with minimum order quantity based on system specifications and the client's production line scale, covering hardware, software licenses, system integration, and training." },
        { heading: "4. Deploy & Evolve", body: "The system is installed and deployed at the client's factory, with Nexuni providing hands-on operational training. After launch, the system leverages continuous AI learning to optimize welding paths and parameters over time, automatically adapting to new workpiece types to improve weld quality and production efficiency." }
      ],
      ja: [
        { heading: "1. MOU 締結 & 現場派遣", body: "深刻な溶接工不足に直面する金属加工工場が Nexuni と MOU を締結。Nexuni は直ちにエンジニアを工場に派遣し、溶接プロセス、人手不足のボトルネック、ワークの種類、既存設備を現場で直接調査します。" },
        { heading: "2. 設計・開発", body: "現場調査の結果を基に、Nexuni の R&D チームがレーザースキャンと協働ロボットアームを組み合わせたスマート溶接ロボットシステムを設計。ワークの誤差を自動補正し、熟練溶接工なしでも操作可能な仕組みで、クライアントの中核的な人手不足課題を解決します。" },
        { heading: "3. 最小発注数での見積", body: "開発完了後、システム仕様とクライアントの生産ラインの規模に基づき、最小発注数での見積もりを提示。ハードウェア、ソフトウェアライセンス、システム統合、トレーニングを含みます。" },
        { heading: "4. 導入・継続進化", body: "クライアントの工場にシステムを設置・導入し、現場での操作トレーニングを実施。稼働後は AI の継続学習機能により、溶接経路やパラメータを最適化し続け、新しいワークにも自動適応して溶接品質と生産効率を向上させます。" }
      ]
    };

    const exampleTitle = activeTab === "standard" ? standardExampleTitle : launchExampleTitle;
    const exampleSteps = activeTab === "standard" ? standardExampleSteps : launchExampleSteps;

    const launchDisclaimer = {
      zh: "首發客戶開發專案的最低訂購量依個案評估，歡迎聯絡我們進一步討論。",
      en: "Minimum order quantity for each development project is assessed on a case-by-case basis. Please reach out to discuss your needs.",
      ja: "各開発プロジェクトの最小発注数はケースバイケースで評価されます。詳細はお問い合わせください。"
    };

    const partnersTitle = {
      zh: "客戶與合作夥伴",
      en: "Customer & Partners",
      ja: "顧客とパートナー"
    };

    const partnersDesc = {
      zh: "以下是與Nexuni攜手合作的尊貴夥伴和客戶。我們非常珍視這些合作關係，因為正是這些合作推動了我們的業務向前發展，不僅提升了運營效率，也實現了更加智慧化和自動化的管理。透過我們的共同努力，我們期待繼續推動業界的創新與進步。",
      en: "Below are our valued partners and customers who have collaborated with Nexuni. We highly value these partnerships as they have propelled our business forward, not only enhancing operational efficiency but also achieving more intelligent and automated management. Through our joint efforts, we look forward to continuing to drive innovation and progress in the industry.",
      ja: "以下はNexuniと協力している尊敬されるパートナーおよび顧客です。これらの協力関係を非常に重視しています。なぜなら、それらが私たちのビジネスを前進させ、運営の効率を向上させるだけでなく、より賢く自動化された管理を実現しているからです。共同の努力を通じて、産業の革新と進歩を引き続き推進していくことを期待しています。"
    };

    const tabColorVar = activeTab === "standard" ? "var(--sub2-color)" : "var(--sub1-color)";

    return (
      <div id="body" className="collaborate_page">
        <AnnouncementBanner lang={lang} />
        <Navbar lang={lang} updateLangFunc={this.update_lang} />
        <Breadcrumb lang={lang} items={[{ label: { zh: "合作流程", en: "Collaboration", ja: "協力フロー" } }]} />

        {/* Hero */}
        <section className="cb_hero">
          <div className="cb_hero_grid" />
          <div className="cb_hero_content">
            <span className="cb_hero_tag">{hero.tag[lang]}</span>
            <h1 className="cb_hero_title">{hero.title[lang]}</h1>
            <p className="cb_hero_subtitle">{hero.subtitle[lang]}</p>
          </div>
        </section>

        {/* Tabs */}
        <section className="cb_tab_bar">
          <div className="cb_tab_bar_inner">
            <button
              className={"cb_tab" + (activeTab === "standard" ? " cb_tab_active cb_tab_standard" : "")}
              onClick={() => this.setState({ activeTab: "standard" })}
            >
              {tabLabels.standard[lang]}
            </button>
            <button
              className={"cb_tab" + (activeTab === "launch" ? " cb_tab_active cb_tab_launch" : "")}
              onClick={() => this.setState({ activeTab: "launch" })}
            >
              {tabLabels.launch[lang]}
            </button>
          </div>
        </section>

        {/* Intro */}
        <section
          className={"cb_intro" + (vis["intro"] ? " cb_visible" : "")}
          ref={el => this.registerRef(el, "intro")}
        >
          <div className="cb_intro_inner">
            <div className="cb_intro_line" style={{ background: activeTab === "standard"
              ? "linear-gradient(to bottom, var(--sub2-color), var(--sub2-color))"
              : "linear-gradient(to bottom, var(--sub1-color), var(--sub1-color))" }} />
            <div className="cb_intro_texts">
              <p>{introText[activeTab][lang]}</p>
              <p>{introText2[activeTab][lang]}</p>
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="cb_steps">
          <div className="cb_steps_inner">
            <h2 className="cb_steps_title">
              {{ zh: "合作流程", en: "Collaboration Process", ja: "協力プロセス" }[lang]}
            </h2>
            <div className="cb_steps_grid" key={activeTab}>
              {steps.map((step, i) => (
                <div
                  key={i}
                  className={"cb_step_card" + (vis["steps"] ? " cb_step_visible" : "")}
                  style={{ '--step-color': step.color, animationDelay: (i * 0.1) + 's' }}
                >
                  <span className="cb_step_num">{step.num}</span>
                  <h4 className="cb_step_card_title">{step.title[lang]}</h4>
                  <p className="cb_step_text">{step.desc[lang]}</p>
                  <div className="cb_step_bar" />
                </div>
              ))}
            </div>
          </div>
          <div ref={el => this.registerRef(el, "steps")} style={{ height: 1 }} />
        </section>

        {/* Example */}
        <section
          className={"cb_example" + (vis["example"] ? " cb_visible" : "")}
          ref={el => this.registerRef(el, "example")}
        >
          <div className="cb_example_inner">
            <h2 className="cb_example_title">{exampleTitle[lang]}</h2>
            <div className="cb_example_steps">
              {exampleSteps[lang].map((es, i) => (
                <div key={activeTab + i} className="cb_example_item">
                  <div className="cb_example_marker">
                    <span className="cb_example_dot" style={{ background: tabColorVar }} />
                    {i < exampleSteps[lang].length - 1 && <span className="cb_example_line" />}
                  </div>
                  <div className="cb_example_content">
                    <h4>{es.heading}</h4>
                    <p>{es.body}</p>
                  </div>
                </div>
              ))}
            </div>
            {activeTab === "launch" && (
              <div className="cb_disclaimer">
                <p>{launchDisclaimer[lang]}</p>
              </div>
            )}
          </div>
        </section>

        {/* Partners */}
        <section
          className={"cb_partners" + (vis["partners"] ? " cb_visible" : "")}
          ref={el => this.registerRef(el, "partners")}
        >
          <div className="cb_partners_inner">
            <h2 className="cb_partners_title">{partnersTitle[lang]}</h2>
            <p className="cb_partners_desc">{partnersDesc[lang]}</p>
          </div>
          <LogoWall mode="grid" />
        </section>

        {/* CTA */}
        <section className="cb_cta">
          <button className="cb_cta_btn" onClick={() => this.navigate('/contact')}>
            {{ zh: "聯絡我們", en: "Contact Us", ja: "お問い合わせ" }[lang]} &rarr;
          </button>
        </section>

        <Footer lang={lang} navigate={(route) => this.navigate(route)} />
      </div>
    );
  }
}

export default CollaboratePage;
